import React, { useMemo, useState } from "react";

import clsx from "clsx";

import PieChart from "../Graphs/PieChart";
import LoadingWheel from "../Shared/LoadingWheel";
import { useNurtureCampaignStats } from "../../data/nurtureCampaigns/useNurtureCampaignStats";
import { INurtureCampaignRuleStatistic } from "../../models/nurtureCampaign/NurtureCampaignStatistics";
import { useNurtureCampaign } from "../../data/nurtureCampaigns/useNurtureCampaign";
import { BaseCard } from "../NurturingCampaign/BaseCard";
import { INewsletterStatistic } from "../../models/emailSettings/EmailSettings";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { NewsletterRecipientsModal } from "./NewsletterRecipientsModal";
/**
 * Accumulates stats from an array of campaign statistic objects
 */
function accumulateStats(statsArray: Omit<INurtureCampaignRuleStatistic, "nurtureRuleId">[]) {
  return statsArray.reduce((acc, current) => ({
    totalCount: acc.totalCount + (current.totalCount || 0),
    deliveredCount: acc.deliveredCount + (current.deliveredCount || 0),
    openCount: acc.openCount + (current.openCount || 0),
    uniqueOpenCount: acc.uniqueOpenCount + (current.uniqueOpenCount || 0),
    clickCount: acc.clickCount + (current.clickCount || 0),
    uniqueClickCount: acc.uniqueClickCount + (current.uniqueClickCount || 0),
  }), {
    totalCount: 0,
    deliveredCount: 0,
    openCount: 0,
    uniqueOpenCount: 0,
    clickCount: 0,
    uniqueClickCount: 0,
  });
};


interface StatCardProps {
  title: string;
  value: string | number;
  className?: string;
}

function StatCard({ title, value, className }: StatCardProps) {
  return (
    <div className={clsx("flex flex-col items-center justify-center px-4", className)}>
      <dt className="font-medium text-gray-700 truncate">
        {title}
      </dt>
      <dd className="mt-1 text-3xl font-semibold text-etpink-600">
        {value}
      </dd>
    </div>
  )
}

interface NewsletterStatsProps {
  stat: INewsletterStatistic;
  date: Date;
}

export function NewsletterStatsDisplay({ stat, date }: NewsletterStatsProps) {
  const [recipientsOpen, setRecipientsOpen] = useState(false);

  const getChartData = (data: INurtureCampaignRuleStatistic) => ({
    labels: ["Total Sent", "Total Delivered", "Total Opens", "Total Clicks", "Total Unique Opens", "Total Unique Clicks"],
    datasets: [
      {
        data: [
          data.totalCount,
          data.deliveredCount,
          data.openCount,
          data.clickCount,
          data.uniqueOpenCount,
          data.uniqueClickCount,
        ],
      },
    ],
  });

  const chartData = useMemo(() => getChartData(stat), [stat]);

  return (
    <>
      <NewsletterRecipientsModal
        open={recipientsOpen}
        setOpen={setRecipientsOpen}
        date={date}
      />
      <BaseCard
        title={`Statistics for ${date?.toLocaleString("default", { month: "long", year: "numeric" })}`}
        rightAdornment={
          // @ts-ignore
          <ButtonInput
            label='View Recipients'
            onClick={() => {
              setRecipientsOpen(true);
            }}
          />
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-x gap-6">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <StatCard
              title="Total Sent"
              value={stat.totalCount}
              className="border-b lg:border-r"
            />
            <StatCard
              title="Total Delivered"
              value={stat.deliveredCount}
              className="border-b"
            />
            <StatCard
              title="Total Opens"
              value={stat.openCount}
              className="border-b lg:border-r"
            />
            <StatCard
              title="Total Clicks"
              value={stat.clickCount}
              className="border-b"
            />
            <StatCard
              title="Total Unique Opens"
              value={stat.uniqueOpenCount}
              className="border-b lg:border-b-0 lg:border-r"
            />
            <StatCard
              title="Total Unique Clicks"
              value={stat.uniqueClickCount}
              className="border-b lg:border-b-0"
            />
          </div>
          <div>
            <PieChart
              data={chartData}
              height={250}
              width="100%"
            />
          </div>
        </div>
      </BaseCard>
    </>
  )

}